<template>
    <TransitionModal>
        <FixedAlwaysRendered
            :data-qa="dataQa"
            :is-shown="isOpen"
            @close="close"
        >
            <ModalWithHeaderAndFooter
                is-filter-modal
                :title-element-id
                @close="close"
            >
                <template #indicator>
                    <slot name="indicator"></slot>
                </template>

                <template #title>
                    <span
                        :id="titleElementId"
                        class="title"
                    >
                        <Icon
                            class="icon"
                            :icon="'filters-alt'"
                        />
                        {{ t('diamond_search.filters') }}

                        &nbsp;

                        <span
                            v-if="activeFilterCount"
                            :data-qa-active-filter-count="activeFilterCount"
                        >
                            ({{ activeFilterCount }})
                        </span>
                    </span>

                    <div class="chips-wrapper">
                        <slot name="chips"></slot>
                    </div>
                </template>

                <template #default>
                    <div
                        v-qa="'modal-filters'"
                        class="filters-container"
                        :class="classFiltersContainer"
                    >
                        <slot name="filters"></slot>
                    </div>
                </template>

                <template #footer>
                    <div class="footer-wrapper">
                        <div class="show-products">
                            <ThButton
                                v-if="!props.fetching"
                                v-qa="'filters-modal-show'"
                                type="bordered"
                                @click="close"
                            >
                                {{
                                    t('catalogue.filters.show_results', {
                                        count: integerFormatter.format(
                                            props.resultCount
                                        ),
                                    })
                                }}
                            </ThButton>
                            <span
                                v-else
                                class="loading-results"
                            >
                                {{ t('catalogue.filters.loading_results') }}
                            </span>
                        </div>
                    </div>
                </template>
            </ModalWithHeaderAndFooter>
        </FixedAlwaysRendered>
    </TransitionModal>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useTranslator } from '@/i18n'
import { useI18n } from 'vue-i18n'

import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import ModalWithHeaderAndFooter from '@/components/layout/modal/ModalWithHeaderAndFooter.vue'
import TransitionModal from '@/components/layout/modal/TransitionModal.vue'
import FixedAlwaysRendered from '@/components/layout/modal/FixedAlwaysRendered.vue'
import Icon from '../icon/Icon.vue'

const props = defineProps<{
    resultCount: number
    fetching?: boolean
    isOpen: boolean
    routeReset: ButtonRouteRelative
    dataQa?: string
    loading?: boolean
    activeFilterCount: number
}>()

const emit = defineEmits<{
    'close': []
}>()

const { t } = useI18n()

const close = () => {
    emit('close')
}

const { integerFormatter } = useTranslator()

const classFiltersContainer = computed(() => ({
    loading: props.loading,
}))

const titleElementId = 'filters-modal-title-id'
</script>

<style lang="scss" scoped>
:deep(.title) {
    margin-right: auto;
}

.title {
    height: 50px;
    display: flex;
    align-items: center;

    .icon {
        margin-right: 10px;
    }
}

.title,
.chips-wrapper {
    padding: 0 16px;
}

.filters-container {
    width: 100%;
    max-width: 610px;
    padding: 0 16px 20px;
    flex-grow: 1;
    position: relative;

    &.loading {
        cursor: wait;
    }

    :deep(.filter) {
        margin: 0 auto 30px;
    }
}

.footer-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    @include min-width(1024) {
        width: 570px;
        margin: 0 auto;
    }

    .loading-results {
        font-family: $effra-font-stack;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        font-size: 0.875rem;
    }
}
</style>
